import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Archive, Database, Download, Trash2, Shield,
  RefreshCw, Loader2, UserX, UserCheck, HardDrive,
  AlertTriangle, CheckCircle, Clock, FileJson, Wrench,
} from "lucide-react";
import { authHeaders } from "@/hooks/useAdminData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDataGovernance() {
  const [stats, setStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [backing, setBacking] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const h = authHeaders();
      const [s, b] = await Promise.all([
        axios.get(`${API}/admin/data-governance/stats`, h),
        axios.get(`${API}/admin/data-governance/backups`, h),
      ]);
      setStats(s.data);
      setBackups(b.data);
    } catch {
      toast.error("Errore nel caricamento dati governance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleArchive = async () => {
    if (!window.confirm("Archiviare tutte le spedizioni e i ritiri con piu di 1 anno? Questa operazione e irreversibile.")) return;
    setArchiving(true);
    try {
      const res = await axios.post(`${API}/admin/data-governance/archive`, {}, authHeaders());
      toast.success(res.data.message);
      loadAll();
    } catch {
      toast.error("Errore durante l'archiviazione");
    } finally {
      setArchiving(false);
    }
  };

  const handleBackup = async () => {
    setBacking(true);
    try {
      const res = await axios.post(`${API}/admin/data-governance/backup`, {}, authHeaders());
      toast.success(res.data.message);
      loadAll();
    } catch {
      toast.error("Errore durante il backup");
    } finally {
      setBacking(false);
    }
  };

  const downloadBackup = (filename) => {
    const token = localStorage.getItem("mv_admin_token");
    window.open(`${API}/admin/data-governance/backup/${filename}?token=${token}`, "_blank");
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Eliminare il backup ${filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/data-governance/backup/${filename}`, authHeaders());
      toast.success("Backup eliminato");
      loadAll();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleClient = async (clientId, activate) => {
    const action = activate ? "reactivate" : "deactivate";
    const label = activate ? "Riattivare" : "Disattivare";
    if (!window.confirm(`${label} questo cliente?`)) return;
    try {
      const res = await axios.post(`${API}/admin/data-governance/client/${clientId}/${action}`, {}, authHeaders());
      toast.success(res.data.message);
      loadAll();
    } catch {
      toast.error("Errore nell'operazione");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="governance-loading">
        <Loader2 size={32} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-data-governance">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-zinc-500">
            Gestione archiviazione, backup e cancellazione dati
          </p>
        </div>
        <button onClick={loadAll} className="p-2 text-zinc-400 hover:text-zinc-600" data-testid="governance-refresh">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Archive} color="amber"
          title="Dati Archiviabili"
          value={`${stats?.shipments?.archivable || 0} spedizioni, ${stats?.pickups?.archivable || 0} ritiri`}
          subtitle={`piu vecchi di 1 anno (su ${stats?.shipments?.total || 0} sped. e ${stats?.pickups?.total || 0} rit. totali)`}
          testid="stat-archivable"
        />
        <StatCard
          icon={Database} color="blue"
          title="Archivio Storico"
          value={`${stats?.archived?.shipments || 0} spedizioni, ${stats?.archived?.pickups || 0} ritiri`}
          subtitle="gia archiviati"
          testid="stat-archived"
        />
        <StatCard
          icon={HardDrive} color="green"
          title="Ultimo Backup"
          value={stats?.last_backup ? new Date(stats.last_backup.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Nessun backup"}
          subtitle={stats?.last_backup ? `${stats.last_backup.size_mb} MB - ${stats.backup_count} backup totali` : "Crea il primo backup"}
          testid="stat-backup"
        />
      </div>

      {/* Section 1: Archiviazione */}
      <section className="bg-white border border-zinc-200 p-6" data-testid="section-archive">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Archive size={20} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Archiviazione Dati</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Sposta le spedizioni e i ritiri con piu di <strong>1 anno</strong> in una collezione di archivio separata.
              I dati archiviati non sono piu visibili nelle liste principali ma restano consultabili.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Come funziona:</strong>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li>Le spedizioni/ritiri creati prima del {new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toLocaleDateString("it-IT")} verranno spostati</li>
                <li>I dati vengono copiati nella collezione <code className="bg-amber-100 px-1 rounded">archived_shipments</code> / <code className="bg-amber-100 px-1 rounded">archived_pickups</code></li>
                <li>Dopo la copia, vengono rimossi dalle collezioni attive</li>
                <li>Si consiglia di creare un <strong>backup prima</strong> dell'archiviazione</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={handleArchive} disabled={archiving || (stats?.shipments?.archivable === 0 && stats?.pickups?.archivable === 0)}
            className="inline-flex items-center gap-2 bg-amber-500 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-amber-600 transition-colors disabled:opacity-50"
            data-testid="archive-btn">
            {archiving ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
            Archivia Dati Vecchi
          </button>
          {stats?.shipments?.archivable === 0 && stats?.pickups?.archivable === 0 && (
            <span className="inline-flex items-center gap-1 text-sm text-green-600">
              <CheckCircle size={14} /> Nessun dato da archiviare
            </span>
          )}
        </div>
      </section>

      {/* Section 2: Backup */}
      <section className="bg-white border border-zinc-200 p-6" data-testid="section-backup">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Database size={20} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Backup Database</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Esporta tutti i dati (clienti, autisti, spedizioni, ritiri, blog, preventivi, contatti, distinte, indirizzi)
              in un file JSON scaricabile. <strong>Consigliato:</strong> backup giornaliero o settimanale.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
          <div className="flex items-start gap-2">
            <Shield size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Strategia consigliata:</strong>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li>Crea un backup <strong>prima</strong> di ogni archiviazione</li>
                <li>Mantieni almeno gli ultimi <strong>30 giorni</strong> di backup</li>
                <li>Scarica periodicamente i backup su un disco esterno o cloud storage</li>
                <li>In caso di emergenza, il file JSON puo essere reimportato nel database</li>
              </ul>
            </div>
          </div>
        </div>

        <button onClick={handleBackup} disabled={backing}
          className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-blue-600 transition-colors disabled:opacity-50 mb-6"
          data-testid="backup-btn">
          {backing ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
          Crea Backup Ora
        </button>

        {/* Backup list */}
        {backups.length > 0 && (
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="backups-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">File</th>
                  <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Data</th>
                  <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Dimensione</th>
                  <th className="text-right px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {backups.map((b) => (
                  <tr key={b.filename} className="hover:bg-zinc-50" data-testid={`backup-row-${b.filename}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-zinc-700">
                        <FileJson size={14} className="text-blue-500" /> {b.filename}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(b.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 font-mono">{b.size_mb} MB</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => downloadBackup(b.filename)} className="p-1.5 text-blue-500 hover:text-blue-700" title="Scarica" data-testid={`download-backup-${b.filename}`}>
                          <Download size={14} />
                        </button>
                        <button onClick={() => deleteBackup(b.filename)} className="p-1.5 text-red-400 hover:text-red-600" title="Elimina" data-testid={`delete-backup-${b.filename}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section 3: Cancellazione Dati */}
      <section className="bg-white border border-zinc-200 p-6" data-testid="section-deletion">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-sm bg-red-100 flex items-center justify-center flex-shrink-0">
            <UserX size={20} className="text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Cancellazione Dati Clienti</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Invece di eliminare definitivamente un cliente, lo <strong>disattiviamo</strong> (soft delete).
              Il cliente non potra piu accedere all'area riservata, ma i suoi dati storici (spedizioni, ritiri, documenti) restano integri per la contabilita e gli obblighi legali.
            </p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <strong>Soft Delete vs Hard Delete:</strong>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li><strong>Soft Delete (consigliato):</strong> Il cliente viene segnato come "inattivo". Puo essere riattivato in qualsiasi momento. I dati restano per audit e fatturazione.</li>
                <li><strong>Hard Delete:</strong> Eliminazione permanente del cliente e di tutti i dati associati. <em>Irreversibile e sconsigliata</em> per motivi di integrita dei dati e conformita fiscale.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-sm text-zinc-500 mb-4">
          <strong>Clienti attualmente inattivi:</strong> {stats?.clients?.inactive || 0} su {stats?.clients?.total || 0} totali.
          <span className="block mt-1 text-xs text-zinc-400">
            Per disattivare o riattivare un cliente, usa i pulsanti nella pagina <a href="/admin/clienti" className="text-[#FF5200] underline">Clienti</a> oppure gestiscili da qui sotto.
          </span>
        </div>

        <InactiveClientsList toggleClient={toggleClient} />
      </section>

      {/* Section 4: Diagnostica Integrita Dati */}
      <DataIntegritySection />
    </div>
  );
}

function StatCard({ icon: Icon, color, title, value, subtitle, testid }) {
  const colors = {
    amber: "bg-amber-100 text-amber-600",
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
  };
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${colors[color]}`}>
          <Icon size={16} />
        </div>
        <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">{title}</p>
      </div>
      <p className="text-sm font-bold text-[#09090B]">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>
    </div>
  );
}

function InactiveClientsList({ toggleClient }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/clients`, authHeaders());
        setClients(res.data.filter((c) => c.is_active === false));
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Loader2 size={16} className="animate-spin text-zinc-400" />;
  if (clients.length === 0) {
    return (
      <div className="text-sm text-zinc-400 flex items-center gap-2">
        <CheckCircle size={14} className="text-green-500" /> Nessun cliente disattivato
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden">
      <table className="w-full text-sm" data-testid="inactive-clients-table">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr>
            <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Cliente</th>
            <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Email</th>
            <th className="text-left px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Disattivato il</th>
            <th className="text-right px-4 py-2 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Azioni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {clients.map((c) => (
            <tr key={c.id} className="hover:bg-zinc-50">
              <td className="px-4 py-3 font-medium text-zinc-700">{c.company || c.name}</td>
              <td className="px-4 py-3 text-zinc-500">{c.email}</td>
              <td className="px-4 py-3 text-zinc-500">{c.deactivated_at ? new Date(c.deactivated_at).toLocaleDateString("it-IT") : "-"}</td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => toggleClient(c.id, true)}
                  className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-green-600 hover:text-green-800"
                  data-testid={`reactivate-client-${c.id}`}>
                  <UserCheck size={14} /> Riattiva
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function DataIntegritySection() {
  const [types, setTypes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/diagnostics/created-at-types`, authHeaders());
      setTypes(r.data);
    } catch {
      toast.error("Errore nel controllo integrita");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const needsFix = types && Object.values(types).some((coll) => coll.date && coll.date > 0);

  const normalize = async () => {
    if (!window.confirm("Convertire tutti i campi created_at di tipo Date in stringhe ISO? L'operazione e sicura e reversibile via backup.")) return;
    setFixing(true);
    try {
      const r = await axios.post(`${API}/admin/diagnostics/normalize-created-at`, {}, authHeaders());
      const total = Object.values(r.data.normalized || {}).reduce((a, b) => a + b, 0);
      toast.success(`Normalizzati ${total} documenti`);
      check();
    } catch {
      toast.error("Errore durante la normalizzazione");
    } finally {
      setFixing(false);
    }
  };

  return (
    <section className="bg-white border border-zinc-200 p-6" data-testid="section-integrity">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Wrench size={20} className="text-purple-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Integrita Dati (created_at)</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Verifica che il campo <code className="bg-zinc-100 px-1 rounded">created_at</code> sia sempre memorizzato come stringa ISO.
            Documenti legacy con tipo <em>Date</em> BSON causano il fallimento delle statistiche mensili.
          </p>
        </div>
      </div>

      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-4">
        {loading && <p className="text-sm text-zinc-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Verifica in corso...</p>}
        {!loading && types && (
          <div className="space-y-1">
            {Object.entries(types).map(([coll, distribution]) => {
              const dateCount = distribution.date || 0;
              const stringCount = distribution.string || 0;
              const total = dateCount + stringCount + (distribution.missing || 0);
              return (
                <div key={coll} className="flex items-center justify-between text-sm" data-testid={`integrity-${coll}`}>
                  <span className="font-mono text-zinc-700">{coll}</span>
                  <span className="text-xs">
                    <span className="text-green-600">string: {stringCount}</span>
                    {" / "}
                    <span className={dateCount > 0 ? "text-red-600 font-bold" : "text-zinc-400"}>date: {dateCount}</span>
                    {" / totale: "}
                    <span className="text-zinc-500">{total}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={check} disabled={loading}
          className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          data-testid="integrity-check-btn">
          <RefreshCw size={14} /> Ricontrolla
        </button>
        <button onClick={normalize} disabled={fixing || !needsFix}
          className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-purple-700 disabled:opacity-40"
          data-testid="integrity-fix-btn">
          {fixing ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
          Normalizza Date -&gt; String
        </button>
        {types && !needsFix && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <CheckCircle size={14} /> Tutti i created_at sono stringhe ISO
          </span>
        )}
      </div>
    </section>
  );
}
